/**
 * Polar.sh Pricing Constants (Client-Side Display Only)
 *
 * Purpose:
 * Hardcoded pricing data used by the subscription UI as a fallback
 * when the CMS is disabled. This file contains NO server-side logic.
 *
 * Server-side product ID resolution and checkout flows are handled by:
 * - supabase/functions/polar-checkout/index.ts
 * - supabase/functions/_shared/polar/subscription-management.ts
 *
 * Imported by:
 * - apps/mobile/hooks/useSubscriptionPricing.ts (PRICING, ProductKey)
 * - apps/mobile/app/(tabs)/subscription.tsx (ProductKey type only)
 */

/**
 * Canonical billing interval type — single source of truth.
 *
 * Import this wherever billing intervals are used on the client side.
 * Do NOT redeclare locally in hooks or components.
 *
 * Note: lib/cms/client.ts and lib/analytics/events/types.ts have
 * intentionally narrower types and should NOT import from here.
 */
export type BillingInterval = 'week' | 'month' | 'quarter' | 'semester' | 'year';

/**
 * Maps a BillingInterval to the suffix used in product keys and display.
 *
 * Examples:
 *   BILLING_INTERVAL_SUFFIX['week']   → 'weekly'
 *   BILLING_INTERVAL_SUFFIX['month']  → 'monthly'
 *   BILLING_INTERVAL_SUFFIX['semester'] → 'semester'
 */
export const BILLING_INTERVAL_SUFFIX: Record<BillingInterval, string> = {
  week: 'weekly',
  month: 'monthly',
  quarter: 'quarterly',
  semester: 'semester',
  year: 'yearly',
};

/**
 * Valid product key strings.
 *
 * These mirror the keys used by the edge-function checkout flow
 * (e.g. "drama_plus_monthly", "dramax_family_yearly").
 */
export type ProductKey =
  | 'drama_plus_weekly'
  | 'drama_plus_monthly'
  | 'drama_plus_quarterly'
  | 'drama_plus_semester'
  | 'drama_plus_yearly'
  | 'dramax_weekly'
  | 'dramax_monthly'
  | 'dramax_quarterly'
  | 'dramax_semester'
  | 'dramax_yearly'
  | 'drama_plus_family_weekly'
  | 'drama_plus_family_monthly'
  | 'drama_plus_family_quarterly'
  | 'drama_plus_family_semester'
  | 'drama_plus_family_yearly'
  | 'dramax_family_weekly'
  | 'dramax_family_monthly'
  | 'dramax_family_quarterly'
  | 'dramax_family_semester'
  | 'dramax_family_yearly';

/**
 * Human-readable display names for each tier.
 * Used by CMS client as fallback tier name mapping.
 */
export const TIER_NAMES: Record<string, string> = {
  anonymous: 'Anonymous',
  free: 'Free',
  drama_plus: 'Drama+',
  dramax: 'DramaX',
  drama_plus_family: 'Drama+ Family',
  dramax_family: 'DramaX Family',
};

/**
 * Pricing information for UI display (CMS fallback)
 *
 * NOTE: Update these if Polar.sh prices change.
 * Pricing Strategy: Weekly (poor value anchor) -> Monthly (default) ->
 *   Quarterly (10% off) -> Semester (20% off) -> Yearly (25% off)
 */
export const PRICING: Record<ProductKey, {
  price: number;
  interval: 'week' | 'month' | 'quarter' | 'semester' | 'year';
  displayPrice: string;
  billedAs?: string;
  perPerson?: string;
  savings?: string;
  tier: 'drama_plus' | 'dramax';
  members?: number;
  quota: number;
  badge?: string;
}> = {
  // Individual Plans - Drama+ (all 5 billing intervals)
  drama_plus_weekly: {
    price: 5.99,
    interval: 'week',
    displayPrice: '$5.99/wk',
    billedAs: '$23.96/month',
    tier: 'drama_plus',
    quota: 100,
    badge: 'New',
  },
  drama_plus_monthly: {
    price: 9.99,
    interval: 'month',
    displayPrice: '$9.99/mo',
    tier: 'drama_plus',
    quota: 100,
  },
  drama_plus_quarterly: {
    price: 8.33,
    interval: 'quarter',
    displayPrice: '$8.33/mo',
    billedAs: '$24.99 every 3 months',
    savings: 'Save 10%',
    tier: 'drama_plus',
    quota: 100,
  },
  drama_plus_semester: {
    price: 7.99,
    interval: 'semester',
    displayPrice: '$7.99/mo',
    billedAs: '$49.99 every 6 months',
    savings: 'Save 20%',
    tier: 'drama_plus',
    quota: 100,
  },
  drama_plus_yearly: {
    price: 7.49,
    interval: 'year',
    displayPrice: '$7.49/mo',
    billedAs: '$89.99/year',
    savings: 'Save 25%',
    tier: 'drama_plus',
    quota: 100,
    badge: '⭐ Best Value',
  },

  // Individual Plans - DramaX (all 5 billing intervals)
  dramax_weekly: {
    price: 9.99,
    interval: 'week',
    displayPrice: '$9.99/wk',
    billedAs: '$39.96/month',
    tier: 'dramax',
    quota: 200,
    badge: 'New',
  },
  dramax_monthly: {
    price: 19.99,
    interval: 'month',
    displayPrice: '$19.99/mo',
    tier: 'dramax',
    quota: 200,
  },
  dramax_quarterly: {
    price: 16.66,
    interval: 'quarter',
    displayPrice: '$16.66/mo',
    billedAs: '$49.99 every 3 months',
    savings: 'Save 10%',
    tier: 'dramax',
    quota: 200,
  },
  dramax_semester: {
    price: 14.99,
    interval: 'semester',
    displayPrice: '$14.99/mo',
    billedAs: '$89.99 every 6 months',
    savings: 'Save 20%',
    tier: 'dramax',
    quota: 200,
  },
  dramax_yearly: {
    price: 14.16,
    interval: 'year',
    displayPrice: '$14.16/mo',
    billedAs: '$169.99/year',
    savings: 'Save 25%',
    tier: 'dramax',
    quota: 200,
    badge: '⭐ Best Value',
  },

  // Family Plans - Drama+ (all 5 billing intervals, 5 members)
  drama_plus_family_weekly: {
    price: 14.99,
    interval: 'week',
    displayPrice: '$14.99/wk',
    billedAs: '$59.96/month',
    perPerson: '$3/person per week',
    tier: 'drama_plus',
    members: 5,
    quota: 500,
    badge: 'New',
  },
  drama_plus_family_monthly: {
    price: 24.99,
    interval: 'month',
    displayPrice: '$24.99/mo',
    perPerson: '$5/person',
    tier: 'drama_plus',
    members: 5,
    quota: 500,
  },
  drama_plus_family_quarterly: {
    price: 21.66,
    interval: 'quarter',
    displayPrice: '$21.66/mo',
    billedAs: '$64.99 every 3 months',
    perPerson: '$4.50/person',
    savings: 'Save 10%',
    tier: 'drama_plus',
    members: 5,
    quota: 500,
  },
  drama_plus_family_semester: {
    price: 19.99,
    interval: 'semester',
    displayPrice: '$19.99/mo',
    billedAs: '$119.99 every 6 months',
    perPerson: '$4/person',
    savings: 'Save 20%',
    tier: 'drama_plus',
    members: 5,
    quota: 500,
  },
  drama_plus_family_yearly: {
    price: 18.33,
    interval: 'year',
    displayPrice: '$18.33/mo',
    billedAs: '$219.99/year',
    perPerson: '$3.75/person',
    savings: 'Save 25%',
    tier: 'drama_plus',
    members: 5,
    quota: 500,
    badge: '⭐ Best Value',
  },

  // Family Plans - DramaX (all 5 billing intervals, 5 members)
  dramax_family_weekly: {
    price: 24.99,
    interval: 'week',
    displayPrice: '$24.99/wk',
    billedAs: '$99.96/month',
    perPerson: '$5/person per week',
    tier: 'dramax',
    members: 5,
    quota: 1000,
    badge: 'New',
  },
  dramax_family_monthly: {
    price: 49.99,
    interval: 'month',
    displayPrice: '$49.99/mo',
    perPerson: '$10/person',
    tier: 'dramax',
    members: 5,
    quota: 1000,
  },
  dramax_family_quarterly: {
    price: 44.99,
    interval: 'quarter',
    displayPrice: '$44.99/mo',
    billedAs: '$134.99 every 3 months',
    perPerson: '$9/person',
    savings: 'Save 10%',
    tier: 'dramax',
    members: 5,
    quota: 1000,
  },
  dramax_family_semester: {
    price: 39.99,
    interval: 'semester',
    displayPrice: '$39.99/mo',
    billedAs: '$239.99 every 6 months',
    perPerson: '$8/person',
    savings: 'Save 20%',
    tier: 'dramax',
    members: 5,
    quota: 1000,
  },
  dramax_family_yearly: {
    price: 37.49,
    interval: 'year',
    displayPrice: '$37.49/mo',
    billedAs: '$449.99/year',
    perPerson: '$7.50/person',
    savings: 'Save 25%',
    tier: 'dramax',
    members: 5,
    quota: 1000,
    badge: '⭐ Best Value',
  },
};
